import { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Link } from '@trashlab/sitesapp/src/components/shared/link';
import { PageSEO } from '@trashlab/sitesapp/src/templates/redebins/components/shared/page-seo';

export const DebrisCalculatorContent = () => {
  // Pavement/Driveway state
  const [squareFeet, setSquareFeet] = useState('');
  const [thickness, setThickness] = useState('');

  // Concrete/Brick state
  const [concreteSquareFeet, setConcreteSquareFeet] = useState('');
  const [concreteThickness, setConcreteThickness] = useState('');

  // Drywall state
  const [drywallSquareFeet, setDrywallSquareFeet] = useState('');

  // Shingles state
  const [shinglesSquareFeet, setShinglesSquareFeet] = useState('');

  // Demolition state
  const [demolitionSquareFeet, setDemolitionSquareFeet] = useState('');
  const [demolitionStories, setDemolitionStories] = useState('1');

  // Calculate cubic yards: (square feet * thickness in inches) / 324
  const calculateCubicYards = (sqft: string, thick: string) => {
    if (!sqft || !thick) return 0;
    const s = parseFloat(sqft);
    const t = parseFloat(thick);
    if (isNaN(s) || isNaN(t)) return 0;
    return (s * t) / 324;
  };

  // Calculate weight for concrete/asphalt: cubic yards * 4000 lbs per cubic yard
  const calculateConcreteWeight = (cubicYards: number) => {
    const pounds = cubicYards * 4000;
    const tons = pounds / 2000;
    return { pounds: Math.round(pounds), tons: tons.toFixed(2) };
  };

  // Calculate weight for drywall: square feet * 0.5 lbs per square foot
  const calculateDrywallWeight = (sqft: string) => {
    if (!sqft) return { pounds: 0, tons: '0.00' };
    const s = parseFloat(sqft);
    if (isNaN(s)) return { pounds: 0, tons: '0.00' };
    const pounds = s * 0.5; // Drywall is lighter
    const tons = pounds / 2000;
    return { pounds: Math.round(pounds), tons: tons.toFixed(2) };
  };

  // Calculate weight for shingles: square feet / 100 * 250 lbs per square
  const calculateShinglesWeight = (sqft: string) => {
    if (!sqft) return { pounds: 0, tons: '0.00' };
    const s = parseFloat(sqft);
    if (isNaN(s)) return { pounds: 0, tons: '0.00' };
    const squares = s / 100; // 1 square = 100 sq ft
    const pounds = squares * 250; // Average 250 lbs per square
    const tons = pounds / 2000;
    return { pounds: Math.round(pounds), tons: tons.toFixed(2) };
  };

  // Calculate weight for demolition: square feet * stories * 10 lbs per sq ft (average)
  const calculateDemolitionWeight = (sqft: string, stories: string) => {
    if (!sqft) return { pounds: 0, tons: '0.00' };
    const s = parseFloat(sqft);
    const st = parseFloat(stories) || 1;
    if (isNaN(s)) return { pounds: 0, tons: '0.00' };
    const pounds = s * st * 10; // Average 10 lbs per sq ft per story
    const tons = pounds / 2000;
    return { pounds: Math.round(pounds), tons: tons.toFixed(2) };
  };

  // Pavement calculations
  const pavementCubicYards = calculateCubicYards(squareFeet, thickness);
  const pavementWeight = calculateConcreteWeight(pavementCubicYards);

  // Concrete/Brick calculations
  const concreteCubicYards = calculateCubicYards(concreteSquareFeet, concreteThickness);
  const concreteWeight = calculateConcreteWeight(concreteCubicYards);

  // Drywall calculations
  const drywallWeight = calculateDrywallWeight(drywallSquareFeet);
  const drywallCubicYards = drywallWeight.pounds / 4000; // Estimate cubic yards

  // Shingles calculations
  const shinglesWeight = calculateShinglesWeight(shinglesSquareFeet);
  const shinglesCubicYards = shinglesWeight.pounds / 4000; // Estimate cubic yards

  // Demolition calculations
  const demolitionWeight = calculateDemolitionWeight(demolitionSquareFeet, demolitionStories);
  const demolitionCubicYards = demolitionWeight.pounds / 4000; // Estimate cubic yards

  return (
    <>
      <PageSEO pathname="/debris-calculator" />
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Intro Section */}
            <div className="grid md:grid-cols-2 gap-12 mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-6 italic">
                  Estimate the Weight Below with our Debris Calculator
                </h2>
              <div className="space-y-4 text-[#333333]">
                <p>
                  Every project creates its own combination of waste, resulting in specific weight and disposal requirements. To help you determine the perfect RED-E-BIN Dumpster size for your project, we've provided easy-to-use debris calculator below. Whether you're clearing out everyday clutter or tackling construction materials, these tools make it simple to assess your needs.
                </p>
                <p>
                  If neither of our two convenient dumpster sizes fits your project just right, don't worry! We offer flexible solutions, including mid-project dumpster swaps or the delivery of a second RED-E-BIN Dumpster at a discounted rate. Our goal is to ensure your cleanup process stays on track, hassle-free, and cost-effective.
                </p>
                <p>
                  From calculating trash volume to estimating waste weight, our refuse sizing tools are here to simplify your planning. Choose RED-E-BINS for reliable, adaptable, and affordable waste removal solutions tailored to your unique project. Find what you need? Check out our{' '}
                  <Link href="/dumpster-sizes" className="text-[#E31837] hover:underline">
                    sizes
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center">
              {/* Calculator Icon - matches Red-E-Bins style */}
              <div className="w-48 h-auto">
                <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Calculator body */}
                  <rect x="10" y="10" width="80" height="100" rx="8" stroke="#E31837" strokeWidth="4" fill="white"/>
                  {/* Display screen */}
                  <rect x="18" y="18" width="64" height="20" rx="2" fill="#E31837"/>
                  {/* Calculator buttons - row 1 */}
                  <rect x="18" y="46" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  <rect x="35" y="46" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  <rect x="52" y="46" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  <rect x="69" y="46" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  {/* Calculator buttons - row 2 */}
                  <rect x="18" y="62" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  <rect x="35" y="62" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  <rect x="52" y="62" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  <rect x="69" y="62" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  {/* Calculator buttons - row 3 */}
                  <rect x="18" y="78" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  <rect x="35" y="78" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  <rect x="52" y="78" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  <rect x="69" y="78" width="12" height="26" rx="2" stroke="#E31837" strokeWidth="2"/>
                  {/* Calculator buttons - row 4 */}
                  <rect x="18" y="94" width="29" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                  <rect x="52" y="94" width="12" height="10" rx="2" stroke="#E31837" strokeWidth="2"/>
                </svg>
              </div>
              <p className="text-[#333333] mt-4 font-semibold text-lg">Debris Calculator</p>
            </div>
          </div>

          {/* Calculator Section */}
          <div className="bg-gray-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-[#333333] mb-2 italic text-center">
              Select Project Type:
            </h2>
            <p className="text-[#333333] mb-6 text-center text-sm italic">
              These calculations are intended to be estimates only. The actual weight and size of your project may vary. Check out our debris calculator below:
            </p>

            <Tabs.Root defaultValue="pavement" className="w-full">
              <Tabs.List className="flex flex-wrap gap-2 mb-6 border-b border-gray-300">
                <Tabs.Trigger
                  value="pavement"
                  className="px-4 py-2 text-[#333333] font-semibold border-b-2 border-transparent data-[state=active]:border-[#E31837] data-[state=active]:text-[#E31837] hover:text-[#E31837] transition-colors"
                >
                  Pavement / Driveway
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="concrete"
                  className="px-4 py-2 text-[#333333] font-semibold border-b-2 border-transparent data-[state=active]:border-[#E31837] data-[state=active]:text-[#E31837] hover:text-[#E31837] transition-colors"
                >
                  Concrete / Brick
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="drywall"
                  className="px-4 py-2 text-[#333333] font-semibold border-b-2 border-transparent data-[state=active]:border-[#E31837] data-[state=active]:text-[#E31837] hover:text-[#E31837] transition-colors"
                >
                  Drywall
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="shingles"
                  className="px-4 py-2 text-[#333333] font-semibold border-b-2 border-transparent data-[state=active]:border-[#E31837] data-[state=active]:text-[#E31837] hover:text-[#E31837] transition-colors"
                >
                  Roofing Shingles
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="demolition"
                  className="px-4 py-2 text-[#333333] font-semibold border-b-2 border-transparent data-[state=active]:border-[#E31837] data-[state=active]:text-[#E31837] hover:text-[#E31837] transition-colors"
                >
                  Structure Demolition
                </Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="pavement" className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#333333] mb-4">
                  Concrete Pavement / Asphalt Driveway Estimator
                </h3>
                <p className="text-[#333333] mb-6">
                  Estimate the weight and volume of an asphalt driveway or concrete disposal project.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-2">
                      Square Feet of Driveway <span className="text-[#E31837]">*</span>
                    </label>
                    <input
                      type="number"
                      value={squareFeet}
                      onChange={(e) => setSquareFeet(e.target.value)}
                      placeholder="Length (in feet) x Width (in feet)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-2">
                      Thickness of Driveway (in inches) <span className="text-[#E31837]">*</span>
                    </label>
                    <input
                      type="number"
                      value={thickness}
                      onChange={(e) => setThickness(e.target.value)}
                      placeholder="Thickness in inches"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837]"
                    />
                  </div>
                  <div className="pt-4 border-t border-gray-300">
                    <h4 className="text-lg font-bold text-[#333333] mb-4">Output</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Cubic Yards
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={pavementCubicYards.toFixed(2)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Weight in Pounds
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={pavementWeight.pounds}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Weight in Tons
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={pavementWeight.tons}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              <Tabs.Content value="concrete" className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#333333] mb-4">
                  Concrete / Brick Estimator
                </h3>
                <p className="text-[#333333] mb-6">
                  Estimate the weight and volume of a concrete or brick disposal project.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-2">
                      Square Feet <span className="text-[#E31837]">*</span>
                    </label>
                    <input
                      type="number"
                      value={concreteSquareFeet}
                      onChange={(e) => setConcreteSquareFeet(e.target.value)}
                      placeholder="Length (in feet) x Width (in feet)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-2">
                      Thickness (in inches) <span className="text-[#E31837]">*</span>
                    </label>
                    <input
                      type="number"
                      value={concreteThickness}
                      onChange={(e) => setConcreteThickness(e.target.value)}
                      placeholder="Thickness in inches"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837]"
                    />
                  </div>
                  <div className="pt-4 border-t border-gray-300">
                    <h4 className="text-lg font-bold text-[#333333] mb-4">Output</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Cubic Yards
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={concreteCubicYards.toFixed(2)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Weight in Pounds
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={concreteWeight.pounds}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Weight in Tons
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={concreteWeight.tons}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              <Tabs.Content value="drywall" className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#333333] mb-4">
                  Drywall Estimator
                </h3>
                <p className="text-[#333333] mb-6">
                  Estimate the weight and volume of drywall disposal.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-2">
                      Square Feet of Drywall <span className="text-[#E31837]">*</span>
                    </label>
                    <input
                      type="number"
                      value={drywallSquareFeet}
                      onChange={(e) => setDrywallSquareFeet(e.target.value)}
                      placeholder="Total square feet of drywall"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837]"
                    />
                  </div>
                  <div className="pt-4 border-t border-gray-300">
                    <h4 className="text-lg font-bold text-[#333333] mb-4">Output</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Cubic Yards
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={drywallCubicYards.toFixed(2)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Weight in Pounds
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={drywallWeight.pounds}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Weight in Tons
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={drywallWeight.tons}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              <Tabs.Content value="shingles" className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#333333] mb-4">
                  Roofing Shingles Estimator
                </h3>
                <p className="text-[#333333] mb-6">
                  Estimate the weight and volume of roofing shingles disposal.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-2">
                      Square Feet of Roof <span className="text-[#E31837]">*</span>
                    </label>
                    <input
                      type="number"
                      value={shinglesSquareFeet}
                      onChange={(e) => setShinglesSquareFeet(e.target.value)}
                      placeholder="Total square feet of roof"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837]"
                    />
                  </div>
                  <div className="pt-4 border-t border-gray-300">
                    <h4 className="text-lg font-bold text-[#333333] mb-4">Output</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Cubic Yards
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={shinglesCubicYards.toFixed(2)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Weight in Pounds
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={shinglesWeight.pounds}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Weight in Tons
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={shinglesWeight.tons}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              <Tabs.Content value="demolition" className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#333333] mb-4">
                  Structure Demolition Estimator
                </h3>
                <p className="text-[#333333] mb-6">
                  Estimate the weight and volume of structure demolition debris.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-2">
                      Square Feet of Structure <span className="text-[#E31837]">*</span>
                    </label>
                    <input
                      type="number"
                      value={demolitionSquareFeet}
                      onChange={(e) => setDemolitionSquareFeet(e.target.value)}
                      placeholder="Total square feet of structure"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#333333] mb-2">
                      Number of Stories <span className="text-[#E31837]">*</span>
                    </label>
                    <input
                      type="number"
                      value={demolitionStories}
                      onChange={(e) => setDemolitionStories(e.target.value)}
                      placeholder="Number of stories"
                      min="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E31837]"
                    />
                  </div>
                  <div className="pt-4 border-t border-gray-300">
                    <h4 className="text-lg font-bold text-[#333333] mb-4">Output</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Cubic Yards
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={demolitionCubicYards.toFixed(2)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Weight in Pounds
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={demolitionWeight.pounds}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#333333] mb-1">
                          Estimated Weight in Tons
                        </label>
                        <input
                          type="text"
                          readOnly
                          value={demolitionWeight.tons}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Tabs.Content>
            </Tabs.Root>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

